import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaWeight, FaArrowLeft, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import SEO, { PageSEO, StructuredData } from '../../components/SEO';

const BMICalculator = () => {
  const [unit, setUnit] = useState('metric'); // metric or imperial
  const [height, setHeight] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [weight, setWeight] = useState('');
  const [result, setResult] = useState(null);

  const calculateBMI = (e) => {
    e.preventDefault();

    let heightInMeters, weightInKg;

    if (unit === 'metric') {
      heightInMeters = parseFloat(height) / 100;
      weightInKg = parseFloat(weight);
    } else {
      // Convert feet and inches to meters
      const totalInches = (parseFloat(heightFeet) * 12) + parseFloat(heightInches || 0);
      heightInMeters = totalInches * 0.0254;
      // Convert lbs to kg
      weightInKg = parseFloat(weight) * 0.453592;
    }

    if (heightInMeters > 0 && weightInKg > 0) {
      const bmi = weightInKg / (heightInMeters * heightInMeters);
      const category = getBMICategory(bmi);
      const healthyWeightRange = getHealthyWeightRange(heightInMeters);

      setResult({
        bmi: bmi.toFixed(1),
        category: category.name,
        color: category.color,
        description: category.description,
        healthyMin: healthyWeightRange.min.toFixed(1),
        healthyMax: healthyWeightRange.max.toFixed(1),
      });
    }
  };

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) {
      return {
        name: 'Underweight',
        color: 'text-blue-600',
        description: 'You may need to gain some weight. Consult a healthcare provider for personalized advice.',
      };
    } else if (bmi < 25) {
      return {
        name: 'Normal Weight',
        color: 'text-green-600',
        description: 'Great! You are within a healthy weight range. Maintain your lifestyle!',
      };
    } else if (bmi < 30) {
      return {
        name: 'Overweight',
        color: 'text-yellow-600',
        description: 'You may benefit from losing some weight through diet and exercise.',
      };
    } else if (bmi < 35) {
      return {
        name: 'Obese (Class I)',
        color: 'text-orange-600',
        description: 'Consider speaking with a healthcare provider about weight management options.',
      };
    } else if (bmi < 40) {
      return {
        name: 'Obese (Class II)',
        color: 'text-red-500',
        description: 'Medical intervention may be beneficial. Please consult a healthcare provider.',
      };
    } else {
      return {
        name: 'Obese (Class III)',
        color: 'text-red-700',
        description: 'Seeking medical advice is strongly recommended for your health.',
      };
    }
  };

  const getHealthyWeightRange = (heightInMeters) => {
    return {
      min: 18.5 * heightInMeters * heightInMeters,
      max: 24.9 * heightInMeters * heightInMeters,
    };
  };

  const getBMIPosition = (bmi) => {
    // Scale: 15 to 40 BMI
    const minBMI = 15;
    const maxBMI = 40;
    const position = ((parseFloat(bmi) - minBMI) / (maxBMI - minBMI)) * 100;
    return Math.min(Math.max(position, 0), 100);
  };

  const resetCalculator = () => {
    setHeight('');
    setHeightFeet('');
    setHeightInches('');
    setWeight('');
    setResult(null);
  };

  const bmiStructuredData = StructuredData.calculator(
    'BMI Calculator',
    'Calculate your Body Mass Index (BMI) to see where you stand on the health scale.',
    '/calculators/bmi'
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...PageSEO.bmiCalculator} structuredData={bmiStructuredData} />
      {/* Header */}
      <section className="bg-gradient-to-br from-blue-500 to-blue-600 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/calculators" className="inline-flex items-center text-blue-100 hover:text-white mb-6 transition-colors">
            <FaArrowLeft className="mr-2" />
            All Calculators
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <FaWeight className="text-2xl" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">BMI Calculator</h1>
              <p className="text-blue-100 mt-1">Calculate your Body Mass Index</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Enter Your Details</h2>

            {/* Unit Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
              <button
                type="button"
                onClick={() => setUnit('metric')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'metric' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Metric (kg/cm)
              </button>
              <button
                type="button"
                onClick={() => setUnit('imperial')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'imperial' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Imperial (lb/ft)
              </button>
            </div>

            <form onSubmit={calculateBMI} className="space-y-5">
              {/* Height Input */}
              {unit === 'metric' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Height (cm)
                  </label>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="e.g., 170"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                    min="100"
                    max="250"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Height
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input
                        type="number"
                        value={heightFeet}
                        onChange={(e) => setHeightFeet(e.target.value)}
                        placeholder="Feet"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                        min="3"
                        max="8"
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        value={heightInches}
                        onChange={(e) => setHeightInches(e.target.value)}
                        placeholder="Inches"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        min="0"
                        max="11"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Weight Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight ({unit === 'metric' ? 'kg' : 'lbs'})
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={unit === 'metric' ? 'e.g., 70' : 'e.g., 154'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  min="30"
                  max={unit === 'metric' ? '300' : '660'}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-xl hover:bg-blue-700 transition-colors font-semibold"
                >
                  Calculate BMI
                </button>
                {result && (
                  <button
                    type="button"
                    onClick={resetCalculator}
                    className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium text-gray-700"
                  >
                    Reset
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Results */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Your Results</h2>

            {result ? (
              <div className="space-y-6">
                {/* BMI Value */}
                <div className="text-center py-6 bg-gray-50 rounded-xl">
                  <div className="text-5xl font-bold text-gray-900 mb-2">{result.bmi}</div>
                  <div className={`text-xl font-semibold ${result.color}`}>{result.category}</div>
                </div>

                {/* Visual Scale */}
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700">BMI Scale</div>
                  <div className="relative h-8 rounded-full overflow-hidden">
                    <div className="absolute inset-0 flex">
                      <div className="flex-1 bg-blue-400"></div>
                      <div className="flex-1 bg-green-400"></div>
                      <div className="flex-1 bg-yellow-400"></div>
                      <div className="flex-1 bg-orange-400"></div>
                      <div className="flex-1 bg-red-400"></div>
                    </div>
                    {/* Marker */}
                    <div
                      className="absolute top-0 bottom-0 w-1 bg-gray-900 shadow-lg transition-all duration-500"
                      style={{ left: `${getBMIPosition(result.bmi)}%` }}
                    >
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded">
                        {result.bmi}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Underweight</span>
                    <span>Normal</span>
                    <span>Overweight</span>
                    <span>Obese</span>
                  </div>
                </div>

                {/* Description */}
                <div className="p-4 bg-blue-50 rounded-xl">
                  <p className="text-gray-700">{result.description}</p>
                </div>

                {/* Healthy Weight Range */}
                <div className="p-4 bg-green-50 rounded-xl">
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-1">
                    <FaCheckCircle />
                    <span>Healthy Weight Range</span>
                  </div>
                  <p className="text-gray-700">
                    For your height, a healthy weight is between{' '}
                    <strong>{result.healthyMin} - {result.healthyMax} kg</strong>
                  </p>
                </div>

                {/* CTA */}
                <Link
                  to="/pricing"
                  className="block w-full text-center bg-accent-500 text-white py-3 rounded-xl hover:bg-accent-600 transition-colors font-semibold"
                >
                  Get Personalized Plan
                </Link>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FaWeight className="text-5xl mx-auto mb-4 text-gray-300" />
                <p>Enter your height and weight to calculate your BMI</p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <FaInfoCircle className="text-blue-600 text-xl" />
            <h3 className="text-lg font-bold text-gray-900">About BMI</h3>
          </div>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600">
              Body Mass Index (BMI) is a simple calculation using height and weight to estimate body fat.
              While BMI doesn't directly measure body fat, it's a useful screening tool to identify potential weight problems.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">BMI Categories</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between"><span>Underweight:</span> <span className="font-medium">&lt; 18.5</span></li>
                  <li className="flex justify-between"><span>Normal weight:</span> <span className="font-medium">18.5 - 24.9</span></li>
                  <li className="flex justify-between"><span>Overweight:</span> <span className="font-medium">25 - 29.9</span></li>
                  <li className="flex justify-between"><span>Obese:</span> <span className="font-medium">&gt;= 30</span></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Limitations</h4>
                <p className="text-sm text-gray-600">
                  BMI doesn't account for muscle mass, bone density, age, gender, or fat distribution.
                  Athletes may have a high BMI due to muscle mass rather than excess fat.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BMICalculator;
